import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/app/lib/utils';
import { ColorName, colors, Variant } from '@/constants/color';
import Icon from './Icon';

const buttonVariantsClassNames: {
  variant: Variant;
  color: ColorName;
  class: string;
}[] = [];
Object.entries(colors).map(([color, variants]) =>
  Object.entries(variants).map(([variant, className]) => {
    buttonVariantsClassNames.push({
      variant: variant as Variant,
      color: color as ColorName,
      class: className,
    });
  })
);

const buttonVariants = cva(
  'group/btn inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[6px] text-sm font-medium ring-offset-background transition-all duration-500 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-secondary disabled:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-primary/50 disabled:text-primary-foreground/50',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:border-secondary',
        secondary: 'bg-secondary text-muted-foreground hover:bg-secondary/80',
        ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      color: {
        blue: '',
        orange: '',
        sky: '',
        black: '',
        green: '',
        destructive: '',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 px-3',
        compact: 'h-8 px-3',
        lg: 'h-11 px-8',
        xs: 'h-[26px] px-2 py-1 text-xs gap-1.5',
        icon: 'h-10 w-10',
        'icon-sm': 'h-8 w-8',
        'icon-xs': 'h-[26px] w-[26px]',
        filter: 'relative h-8 w-8 rounded-[6px] border-input p-2 text-muted-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
    compoundVariants: [...buttonVariantsClassNames],
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  color?: ColorName;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, color, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn('cursor-pointer', buttonVariants({ variant, size, className, color }))}
        ref={ref}
        {...props}
        onClick={e => {
          if (loading || props.disabled) {
            e.preventDefault();
            return;
          }
          props.onClick?.(e);
        }}
        disabled={props.disabled || loading}
      >
        {loading && (
          <Icon
            name="loader-2-fill"
            size={4}
            aria-label="Loading"
            className="animate-spin w-4 h-4"
          />
        )}
        {props.children}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
